import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, Target, Eye, Zap, Users, Globe, 
  Award, ArrowRight, CheckCircle
} from 'lucide-react';

const values = [
  {
    icon: Shield,
    title: 'Security First',
    description: 'Every solution we build prioritizes security and data protection.'
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'Pushing boundaries with cutting-edge technology solutions.'
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We work alongside our clients as true technology partners.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Committed to delivering the highest quality in every project.'
  }
];

const timeline = [
  { year: '2024', event: 'SentinelX Technology Pvt Ltd founded in Faridabad, Haryana' },
  { year: '2024', event: 'Started development of TrackOtic - Cold Chain Monitoring Platform' },
  { year: '2025', event: 'TrackOtic MVP launched with real-time GPS & temperature tracking' },
  { year: '2025', event: 'Expanding to pharmaceutical logistics partners across India' }
];

const certifications = [
  'GDP Compliant', 'D&C Act Ready', 'WHO Guidelines', 'ISO 27001 (In Progress)'
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#05050A]">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Badge className="mb-6 bg-primary/10 text-primary border-primary/30 font-mono text-xs">
                ABOUT US
              </Badge>
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6">
                Pioneering the Future of
                <span className="block text-primary">Intelligent Systems</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                SentinelX Technology is a Faridabad-based startup building intelligent 
                IoT solutions for secure delivery systems. Our flagship product <strong>TrackOtic</strong> enables 
                real-time cold chain monitoring for pharmaceutical logistics with GPS tracking 
                and temperature compliance.
              </p>
              <div className="flex gap-4">
                <Link to="/contact">
                  <Button 
                    className="bg-primary text-primary-foreground font-bold uppercase tracking-wider btn-chamfer"
                    data-testid="about-cta-btn"
                  >
                    Work With Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="aspect-square bg-card/30 border border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield className="w-48 h-48 text-primary/20" />
                </div>
                {/* Stats overlay */}
                <div className="absolute bottom-6 left-6 right-6 bg-black/60 backdrop-blur-sm border border-white/10 p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="font-heading text-2xl font-bold text-primary">6+</div>
                      <div className="text-xs text-muted-foreground">Years</div>
                    </div>
                    <div>
                      <div className="font-heading text-2xl font-bold text-primary">500+</div>
                      <div className="text-xs text-muted-foreground">Projects</div>
                    </div>
                    <div>
                      <div className="font-heading text-2xl font-bold text-primary">50+</div>
                      <div className="text-xs text-muted-foreground">Team</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-black/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card/30 border border-white/10 p-8"
            >
              <div className="w-12 h-12 bg-primary/10 border border-primary/30 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Our Mission</h3>
              <p className="text-muted-foreground leading-relaxed">
                To engineer intelligent systems that protect critical infrastructure, 
                optimize operations, and empower organizations to thrive in an 
                increasingly connected world. We believe technology should serve 
                humanity by making systems more secure, efficient, and accessible.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-card/30 border border-white/10 p-8"
            >
              <div className="w-12 h-12 bg-secondary/10 border border-secondary/30 flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">Our Vision</h3>
              <p className="text-muted-foreground leading-relaxed">
                To be the global leader in integrated technology solutions, where 
                IoT, cybersecurity, and enterprise software converge to create 
                seamless, intelligent ecosystems. We envision a future where every 
                organization has access to enterprise-grade security and innovation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 font-mono text-xs">
              OUR VALUES
            </Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card/30 border border-white/10 p-6 text-center hover:border-primary/30 transition-colors"
              >
                <div className="w-14 h-14 bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-black/30">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/30 font-mono text-xs">
              OUR JOURNEY
            </Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight">
              Company Timeline
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2" />
            
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center gap-8 mb-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="font-mono text-primary font-bold mb-1">{item.year}</div>
                  <p className="text-muted-foreground">{item.event}</p>
                </div>
                
                <div className="absolute left-0 md:left-1/2 w-3 h-3 bg-primary md:-translate-x-1/2 -translate-y-1/2 top-1/2" />
                
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 font-mono text-xs">
              COMPLIANCE
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
              Certifications & Compliance
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 px-4 py-2 bg-card/30 border border-white/10"
              >
                <CheckCircle className="w-4 h-4 text-primary" />
                <span className="text-sm font-mono">{cert}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-black/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/30 font-mono text-xs">
                GLOBAL REACH
              </Badge>
              <h2 className="font-heading text-4xl font-semibold tracking-tight mb-6">
                Serving Clients Worldwide
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                With offices across Asia Pacific and partnerships spanning globally, 
                SentinelX delivers enterprise solutions to organizations of all sizes, 
                from innovative startups to Fortune 500 companies.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { region: 'Asia Pacific', count: '200+ clients' },
                  { region: 'North America', count: '150+ clients' },
                  { region: 'Europe', count: '100+ clients' },
                  { region: 'Middle East', count: '50+ clients' }
                ].map((item) => (
                  <div key={item.region} className="bg-card/30 border border-white/10 p-4">
                    <div className="font-heading font-semibold">{item.region}</div>
                    <div className="text-sm text-primary font-mono">{item.count}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square bg-card/20 border border-white/10 flex items-center justify-center">
                <Globe className="w-48 h-48 text-primary/30" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Join the SentinelX
            <span className="text-primary"> Ecosystem</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Whether you&apos;re looking to secure your infrastructure or build the next 
            generation of intelligent systems, we&apos;re here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-primary text-primary-foreground font-bold uppercase tracking-wider btn-chamfer glow-cyan h-14 px-8"
              >
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                size="lg"
                variant="outline"
                className="border-white/20 hover:border-primary/50 h-14 px-8"
              >
                Join as Client
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
